import { formatPhoneDisplay } from '@cowbell/broker/phone';
import { Avatar, Button, Card, Group, Stack, Text } from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faLocationDot,
  faPhone,
  faShieldCheck,
} from '@fortawesome/pro-regular-svg-icons';

import { REDACTED_FIELD } from '../config/logrocket';
import { useCowbellTranslations } from '../i18n/translations';
import type { AgencyLocationDto } from './api/myAgent.api';
import type { ResolvedAgent, ResolvedAgents } from './resolveAgentSource';
import { initialsOf } from './myAgent.styles';

interface MyAgentSummaryProps {
  readonly agent: ResolvedAgents;
  readonly location?: AgencyLocationDto;
  readonly onShowOnMap?: (agencyId: string) => void;
  /** Whether the agent search is on the page, which the empty state points to. */
  readonly canSearch: boolean;
}

interface AgentCardProps {
  readonly agent: ResolvedAgent;
  readonly caption: string;
  readonly location?: AgencyLocationDto;
  readonly onShowOnMap?: (agencyId: string) => void;
}

const addressOf = (location?: AgencyLocationDto): string | undefined => {
  if (!location) {
    return undefined;
  }
  const street = [location.address1, location.address2]
    .filter(Boolean)
    .join(' ');
  const region = [location.city, location.state].filter(Boolean).join(', ');
  const cityLine = [region, location.zipCode].filter(Boolean).join(' ');
  const full = [street, cityLine].filter(Boolean).join(' · ');
  return full.length > 0 ? full : undefined;
};

interface DetailProps {
  readonly icon?: typeof faEnvelope;
  readonly children: React.ReactNode;
}

function Detail({ icon, children }: Readonly<DetailProps>) {
  return (
    <Group gap={6} wrap="nowrap">
      {icon && (
        <Text c="dimmed" span>
          <FontAwesomeIcon icon={icon} />
        </Text>
      )}
      <Text size="sm" c="dimmed" span>
        {children}
      </Text>
    </Group>
  );
}

function AgentCard({
  agent,
  caption,
  location,
  onShowOnMap,
}: Readonly<AgentCardProps>) {
  const address = addressOf(location);

  return (
    <Card withBorder radius="md" padding="md" className={REDACTED_FIELD}>
      <Group justify="space-between" wrap="wrap" gap="lg">
        <Group gap="md" wrap="nowrap">
          <Avatar radius="xl" size={36} color="dark">
            {initialsOf(agent.agencyName)}
          </Avatar>
          <Stack gap={0}>
            <Text size="xs" fw={600} tt="uppercase" c="dimmed">
              {caption}
            </Text>
            <Text fw={600}>{agent.agencyName}</Text>
          </Stack>
        </Group>

        <Group gap="lg" wrap="wrap">
          {agent.agentFullName && (
            <Text size="sm" span>
              {agent.agentFullName}
            </Text>
          )}
          {agent.agentEmail && (
            <Detail icon={faEnvelope}>{agent.agentEmail}</Detail>
          )}
          {agent.agentPhone && (
            <Detail icon={faPhone}>
              {formatPhoneDisplay(agent.agentPhone)}
            </Detail>
          )}
          {address && <Detail icon={faLocationDot}>{address}</Detail>}
        </Group>

        {onShowOnMap && agent.agencyId && (
          <Button
            variant="default"
            size="xs"
            onClick={() => onShowOnMap(agent.agencyId as string)}
          >
            Show on map
          </Button>
        )}
      </Group>
    </Card>
  );
}

/**
 * The resolved agent, or an empty state. `interested` counts as no agent: those
 * agencies are candidates in the table below, not someone who represents the
 * policyholder.
 */
export function MyAgentSummary({
  agent,
  location,
  onShowOnMap,
  canSearch,
}: Readonly<MyAgentSummaryProps>) {
  const { t } = useCowbellTranslations();
  const term = t('agent').toLowerCase();

  if (!agent.primary.agencyName) {
    return (
      <Card withBorder radius="md" padding="md">
        <Group gap="sm" wrap="wrap">
          <Text c="blue" span>
            <FontAwesomeIcon icon={faShieldCheck} />
          </Text>
          <Text fw={600}>{`No ${term} on file`}</Text>
          {canSearch && (
            <Text size="sm" c="dimmed">
              {`Search below and we'll help you find a Cowbell Cyber ${term} near you.`}
            </Text>
          )}
        </Group>
      </Card>
    );
  }

  return (
    <Stack gap="xs">
      <AgentCard
        agent={agent.primary}
        caption={`Your ${term}`}
        location={location}
        onShowOnMap={onShowOnMap}
      />
      {agent.secondary && (
        <AgentCard
          agent={agent.secondary}
          caption={`${t('agent')} on your active quote`}
        />
      )}
    </Stack>
  );
}

export default MyAgentSummary;
